import { UnityBridge } from "@Context/common/general/context";
import useUserPreferences from "@hooks/common/user/use-user-preferences";
import { useEffect, useRef, useState } from "react";
import { useUnmount } from "react-use";

type SettingsValues = Record<string, number | boolean>;

type SettingsCallbacks<T> = {
  onApply?: (values: T) => void;
  onPersist?: (values: T) => void;
};

export default function useGameSettings<T extends SettingsValues>(defaults: T, callbacks: SettingsCallbacks<T> = {}) {
  const { preferences, updatePreferencesAsync, error, refetch } = useUserPreferences();
  const [draft, setDraft] = useState<T | null>(null);
  const latestDraft = useRef<T | null>(null);
  const dirtyFields = useRef(new Set<keyof T>());
  const latestCallbacks = useRef(callbacks);
  latestCallbacks.current = callbacks;

  useEffect(() => {
    if (!preferences) return;
    const current = latestDraft.current;
    const hydrated = Object.fromEntries(
      Object.entries(defaults).map(([id, fallback]) => {
        if (current && dirtyFields.current.has(id)) return [id, current[id]];
        const value = preferences[id];
        return [id, typeof fallback === "boolean" ? !!value : typeof value === "number" ? value : fallback];
      })
    ) as T;
    if (current && Object.keys(hydrated).every(id => Object.is(current[id], hydrated[id]))) return;
    latestDraft.current = hydrated;
    setDraft(hydrated);
  }, [preferences, defaults]);

  const setValue = <K extends keyof T>(id: K, value: T[K]) => {
    const current = latestDraft.current;
    if (!current || Object.is(current[id], value)) return;
    const next = { ...current, [id]: value };
    dirtyFields.current.add(id);
    latestDraft.current = next;
    setDraft(next);
  };

  useEffect(() => {
    if (!draft) return;
    UnityBridge.dispatchWebEvent({
      type: UnityBridge.OutgoingWebEvents.SetPreferences,
      payload: draft,
    });
    latestCallbacks.current.onApply?.(draft);
  }, [draft]);

  useUnmount(() => {
    const values = latestDraft.current;
    if (!values || !dirtyFields.current.size) return;
    const updates = Object.entries(values)
      .filter(([id]) => dirtyFields.current.has(id))
      .map(([id, value]) => ({ id, value }));
    void updatePreferencesAsync(updates).catch(() => {});
    latestCallbacks.current.onPersist?.(values);
  });

  return { values: draft ?? defaults, setValue, ready: draft !== null, error, refetch };
}
